import { useState, useRef, useCallback, useEffect } from 'react';

const DEBOUNCE_MS = 300;
const MIN_CHARS = 3;
const CACHE_TTL = 5 * 60 * 1000;

const cache = new Map();

const getCached = (key) => {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.ts > CACHE_TTL) {
    cache.delete(key);
    return null;
  }
  return entry.data;
};

const setCache = (key, data) => {
  cache.set(key, { data, ts: Date.now() });
};

let googleLoaded = false;
let googleLoadPromise = null;

const loadGoogleMaps = (apiKey) => {
  if (googleLoaded && window.google?.maps?.places) return Promise.resolve();
  if (googleLoadPromise) return googleLoadPromise;

  googleLoadPromise = new Promise((resolve, reject) => {
    if (window.google?.maps?.places) {
      googleLoaded = true;
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => { googleLoaded = true; resolve(); };
    script.onerror = reject;
    document.head.appendChild(script);
  });

  return googleLoadPromise;
};

export default function useAddressAutocomplete({ apiKey, countryCode = '' }) {
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isReady, setIsReady] = useState(false);

  const autocompleteRef = useRef(null);
  const placesRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!apiKey) return;
    loadGoogleMaps(apiKey).then(() => {
      autocompleteRef.current = new window.google.maps.places.AutocompleteService();
      const div = document.createElement('div');
      placesRef.current = new window.google.maps.places.PlacesService(div);
      setIsReady(true);
    }).catch(() => {
      setIsReady(false);
    });
  }, [apiKey]);

  const fetchSuggestions = useCallback((input) => {
    if (timerRef.current) clearTimeout(timerRef.current);

    if (!input || input.length < MIN_CHARS || !autocompleteRef.current) {
      setSuggestions([]);
      return;
    }

    const cacheKey = `${input}::${countryCode}`;
    const cached = getCached(cacheKey);
    if (cached) {
      setSuggestions(cached);
      return;
    }

    timerRef.current = setTimeout(() => {
      setIsLoading(true);
      const req = {
        input,
        types: ['address'],
        ...(countryCode ? { componentRestrictions: { country: countryCode.toLowerCase() } } : {})
      };
      autocompleteRef.current.getPlacePredictions(req, (predictions, status) => {
        setIsLoading(false);
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          const items = predictions.map(p => ({
            placeId: p.place_id,
            description: p.description,
            mainText: p.structured_formatting?.main_text || '',
            secondaryText: p.structured_formatting?.secondary_text || ''
          }));
          setCache(cacheKey, items);
          setSuggestions(items);
        } else {
          setSuggestions([]);
        }
      });
    }, DEBOUNCE_MS);
  }, [countryCode]);

  const selectSuggestion = useCallback((suggestion) => {
    return new Promise((resolve, reject) => {
      if (!placesRef.current || !suggestion?.placeId) {
        reject(new Error('Places service unavailable'));
        return;
      }
      placesRef.current.getDetails(
        { placeId: suggestion.placeId, fields: ['address_components', 'formatted_address'] },
        (place, status) => {
          if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) {
            reject(new Error('Failed to get place details'));
            return;
          }
          const parsed = {
            address1: '',
            city: '',
            state: '',
            postal: '',
            country: countryCode
          };
          let streetNumber = '';
          let route = '';
          for (const comp of (place.address_components || [])) {
            const t = comp.types;
            if (t.includes('street_number')) streetNumber = comp.long_name;
            if (t.includes('route')) route = comp.long_name;
            if (t.includes('locality')) parsed.city = comp.long_name;
            if (t.includes('administrative_area_level_1')) parsed.state = comp.short_name;
            if (t.includes('postal_code')) parsed.postal = comp.long_name;
            if (t.includes('country')) parsed.country = comp.short_name;
          }
          parsed.address1 = `${streetNumber} ${route}`.trim();
          setSuggestions([]);
          resolve(parsed);
        }
      );
    });
  }, [countryCode]);

  const clearSuggestions = useCallback(() => setSuggestions([]), []);

  return { suggestions, isLoading, isReady, fetchSuggestions, selectSuggestion, clearSuggestions };
}
